import { useMemo, useState } from 'react'
import { MapPinIcon, MapPinOffIcon, TagsIcon } from 'lucide-react'
import { Box, Chip, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'

import { Button, Card, Modal, Skeleton, Switch } from '@repo/ui'
import { Search, SelectField } from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import {
  useSeoKeywords,
  useSetKeywordMarkets,
  useSetKeywordOwnIt,
  useSetKeywordStatus,
  useSetKeywordTags,
} from 'hooks/api'
import type {
  SeoDemandByTag,
  SeoDemandSummary,
  SeoKeywordRow,
  SeoReviewStatus,
  SeoStatus,
  SeoTaxonomyTag,
} from 'services'

import type { ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'

import { Cell, EmptyRow, HeadCell, SectionNote, StatTable, useTone } from '../shared'
import { fmt, fmtUsd, Sparkline } from './seoModel'
import { KeywordMenu, keywordEvidenceLine, REVIEW_LABEL, REVIEW_ORDER, ReviewChip } from './seoReview'

const ALL = '__all'
const TOP = 200
const ADMIN_ONLY = 'Only platform admins can change the keyword set.'

const options = (values: Array<string | null>, allLabel: string) => [
  { value: ALL, label: allLabel },
  ...[...new Set(values.filter((v): v is string => !!v))]
    .sort()
    .map((v) => ({ value: v, label: v })),
]

const REASON_LABEL: Record<string, string> = { rule: 'Rule', 'own-it': 'Own-it', manual: 'Set' }

const TrendFlag = ({ flag }: { flag: SeoKeywordRow['trendFlag'] }) => {
  const toneOf = useTone()
  if (!flag) return null
  // VERA tones (green / red / orange via statusTone), not MUI's success / error / warning.
  const tone = toneOf(flag === 'rising' ? 'positive' : flag === 'falling' ? 'negative' : 'caution').color
  return (
    <InfoTooltip
      text={
        flag === 'verify'
          ? 'A step jump in the monthly series: check the data before trusting the trend.'
          : `${flag === 'rising' ? 'Up' : 'Down'} 30% or more over 12 months.`
      }
    >
      <Typography
        component="span"
        variant="body3"
        sx={{ color: tone, ml: 1, fontSize: 11, cursor: 'help' }}
      >
        {flag === 'verify' ? 'verify' : flag === 'rising' ? '▲' : '▼'}
      </Typography>
    </InfoTooltip>
  )
}

const TagChips = ({ row, labels }: { row: SeoKeywordRow; labels: Map<string, string> }) => {
  const tags = [
    ...row.industries.filter((t) => t !== 'cross-industry'),
    ...row.solutions,
    ...row.products,
  ]
  if (!tags.length) return null
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', rowGap: '4px', columnGap: '4px', mt: '4px' }}>
      {tags.slice(0, 5).map((t) => (
        <Chip
          key={t}
          size="small"
          variant="outlined"
          label={labels.get(t) ?? t}
          sx={{ height: 18, fontSize: 10.5, fontWeight: 400 }}
        />
      ))}
    </Stack>
  )
}

/** Edit one keyword's industry / solution / product tags; saving marks them hand-set. */
const EditTagsModal = ({
  row,
  taxonomy,
  onClose,
}: {
  row: SeoKeywordRow | null
  taxonomy: SeoTaxonomyTag[]
  onClose: () => void
}) => {
  const save = useSetKeywordTags()
  const { open: notify } = useSnackbar()
  const [draft, setDraft] = useState<{ industries: string[]; solutions: string[]; products: string[] } | null>(null)
  const value = draft ??
    (row ? { industries: row.industries, solutions: row.solutions, products: row.products } : null)
  const opts = (axis: SeoTaxonomyTag['axis']) =>
    taxonomy.filter((t) => t.axis === axis).map((t) => ({ value: t.slug, label: t.label }))
  const close = () => {
    setDraft(null)
    onClose()
  }
  if (!row || !value) return null
  return (
    <Modal
      open={!!row}
      onClose={close}
      title={`Tags: ${row.keyword}`}
      description="Set by rules at import. Saving here marks the tags as hand-set, so later rule runs leave this keyword alone."
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { id: row.id, ...value },
                {
                  onSuccess: () => {
                    notify({ type: 'success', text: `Saved tags for “${row.keyword}”.` })
                    close()
                  },
                  onError: (e) => notify({ type: 'error', text: (e as Error).message }),
                },
              )
            }
          >
            Save tags
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <SelectField
          label="Industry"
          multiple
          value={value.industries}
          onChange={(v) => setDraft({ ...value, industries: v as string[] })}
          options={opts('industry')}
        />
        <SelectField
          label="Solution"
          multiple
          value={value.solutions}
          onChange={(v) => setDraft({ ...value, solutions: v as string[] })}
          options={opts('solution')}
        />
        <SelectField
          label="Product"
          multiple
          value={value.products}
          onChange={(v) => setDraft({ ...value, products: v as string[] })}
          options={opts('product')}
        />
      </Stack>
    </Modal>
  )
}

const ByTagTable = ({
  title,
  rows,
  labels,
}: {
  title: string
  rows: SeoDemandByTag[]
  labels: Map<string, string>
}) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    <StatTable>
      <TableHead>
        <TableRow>
          <HeadCell>Tag</HeadCell>
          <HeadCell align="right">Terms</HeadCell>
          <HeadCell align="right">Searches / mo</HeadCell>
          <HeadCell align="right">Tracked</HeadCell>
          <HeadCell align="right">Avg position</HeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={5} text="No tagged keywords." />
        ) : (
          rows.slice(0, 10).map((r) => (
            <TableRow key={r.slug}>
              <Cell strong>{labels.get(r.slug) ?? r.slug}</Cell>
              <Cell align="right">{fmt(r.terms)}</Cell>
              <Cell align="right">{fmt(r.searches)}</Cell>
              <Cell align="right">{fmt(r.tracked)}</Cell>
              <Cell align="right">{r.avgPosition === null ? '—' : r.avgPosition.toFixed(1)}</Cell>
            </TableRow>
          ))
        )}
      </TableBody>
    </StatTable>
  </Box>
)

/** Where demand sits, how much of the buyer-fit demand the tracked set covers, and what ranking would be worth. */
const DemandPanel = ({ d, labels }: { d: SeoDemandSummary; labels: Map<string, string> }) => (
  <Stack spacing={4}>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      Demand · {fmt(d.totalTerms)} {REVIEW_LABEL[d.status].toLowerCase()} keywords ·{' '}
      {fmt(d.totalSearches)} searches a month
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={6}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell>Monthly searches</HeadCell>
              <HeadCell align="right">Terms</HeadCell>
              <HeadCell align="right">Searches / mo</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {d.buckets.map((b) => (
              <TableRow key={b.id}>
                <Cell>{b.label}</Cell>
                <Cell align="right">{fmt(b.terms)}</Cell>
                <Cell align="right">{fmt(b.searches)}</Cell>
              </TableRow>
            ))}
          </TableBody>
        </StatTable>
      </Box>
      <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
        <SectionNote>
          <strong>Buyer fit &ge; {d.fit.minBuyerFit}:</strong> {fmt(d.fit.terms)} terms,{' '}
          {fmt(d.fit.searches)} searches a month. The tracked set covers {fmt(d.fit.trackedTerms)} of
          them and{' '}
          {d.fit.coveredShare === null ? '—' : `${Math.round(d.fit.coveredShare * 100)}%`} of their
          searches.
        </SectionNote>
        <SectionNote>
          <strong>Estimated clicks a month</strong>
          <InfoTooltip term="seo-estimated-clicks" />: {fmt(d.clicks.now)} at Vera&rsquo;s current
          positions, {fmt(d.clicks.at3)} if every term ranked #3, {fmt(d.clicks.at1)} at #1.
        </SectionNote>
        <SectionNote>
          <strong>Equivalent ad value</strong>
          <InfoTooltip term="seo-equivalent-ad-value" />: {fmtUsd(d.adValue.now)} a month now,{' '}
          {fmtUsd(d.adValue.at1)} at #1. An estimate; Vera pays nothing per organic click.
        </SectionNote>
        <SectionNote>{d.ctrLabel}</SectionNote>
      </Stack>
    </Stack>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={6}>
      <ByTagTable title="Demand by industry" rows={d.byIndustry} labels={labels} />
      <ByTagTable title="Demand by solution" rows={d.bySolution} labels={labels} />
    </Stack>
  </Stack>
)

/**
 * The keyword vocabulary for one market, as a review list: approved keywords are the
 * vocabulary (metrics monthly; rank checks follow the tracking rule), proposed ones came
 * from the gap finder or an imported list, rejected ones were said no to. Volume / KD /
 * CPC are the latest monthly figures — never a live call.
 */
export const SeoKeywordsCard = ({ market, status }: { market: string; status: SeoStatus }) => {
  const { data, isLoading, error } = useSeoKeywords(market)
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const setOwnIt = useSetKeywordOwnIt()
  const setStatus = useSetKeywordStatus()
  const setMarkets = useSetKeywordMarkets()
  const { open: notify } = useSnackbar()

  /**
   * One click per market of the keyword's own language: "Stop measuring in UK" / "Measure
   * in UK". Removing a market also takes it out of that market's explicit tracked set; the
   * last market cannot be removed (a keyword measured nowhere is a rejected keyword).
   */
  const marketItems = (r: SeoKeywordRow): ActionDropdownItem[] => {
    const same = status.markets.filter((m) => m.active && m.language === r.language)
    if (same.length < 2) return []
    return same.map((m, i) => {
      const on = r.markets.includes(m.code)
      const next = on ? r.markets.filter((c) => c !== m.code) : [...r.markets, m.code]
      const last = on && next.length === 0
      const code = m.code.toUpperCase()
      return {
        label: on ? `Stop measuring in ${code}` : `Measure in ${code}`,
        icon: on ? <MapPinOffIcon size={18} strokeWidth={1} /> : <MapPinIcon size={18} strokeWidth={1} />,
        divider: i === 0,
        disabled: !isAdmin || last || setMarkets.isPending,
        tooltip: !isAdmin
          ? ADMIN_ONLY
          : last
            ? 'A keyword has to be measured somewhere. Reject it instead.'
            : undefined,
        onClick: () =>
          setMarkets.mutate(
            { id: r.id, markets: next },
            {
              onSuccess: () =>
                notify({
                  type: 'success',
                  text: on
                    ? `"${r.keyword}" is no longer measured or rank-checked in ${code}.`
                    : `"${r.keyword}" is now measured in ${code}.`,
                }),
              onError: (e) => notify({ type: 'error', text: (e as Error).message }),
            },
          ),
      }
    })
  }

  const [review, setReview] = useState<SeoReviewStatus>('approved')
  const [query, setQuery] = useState('')
  const [cluster, setCluster] = useState(ALL)
  const [industry, setIndustry] = useState(ALL)
  const [solution, setSolution] = useState(ALL)
  const [product, setProduct] = useState(ALL)
  const [scope, setScope] = useState<'all' | 'tracked' | 'own'>('all')
  const [editing, setEditing] = useState<SeoKeywordRow | null>(null)

  const labels = useMemo(
    () => new Map(status.taxonomy.map((t) => [t.slug, t.label])),
    [status.taxonomy],
  )
  const tagOptions = (axis: 'industry' | 'solution' | 'product', all: string) => [
    { value: ALL, label: all },
    ...status.taxonomy.filter((t) => t.axis === axis).map((t) => ({ value: t.slug, label: t.label })),
  ]

  const rows = data?.rows ?? []
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows
      .filter((r) => r.status === review)
      .filter((r) => (q ? r.keyword.toLowerCase().includes(q) : true))
      .filter((r) => cluster === ALL || r.cluster === cluster)
      .filter((r) => industry === ALL || r.industries.includes(industry))
      .filter((r) => solution === ALL || r.solutions.includes(solution))
      .filter((r) => product === ALL || r.products.includes(product))
      .filter((r) => (scope === 'tracked' ? r.tracked : scope === 'own' ? r.ownIt : true))
      .sort((a, b) => (b.metrics?.volume ?? -1) - (a.metrics?.volume ?? -1))
  }, [rows, review, query, cluster, industry, solution, product, scope])

  const counts = data?.counts ?? { approved: 0, proposed: 0, dismissed: 0 }
  const reviewOptions: readonly ViewToggleOption<SeoReviewStatus>[] = REVIEW_ORDER.map((s) => ({
    value: s,
    label: `${REVIEW_LABEL[s]} (${fmt(counts[s])})`,
  }))
  const tracking = status.tracking.markets.find((m) => m.market === market)

  const changeStatus = (r: SeoKeywordRow, next: 'approved' | 'dismissed') =>
    setStatus.mutate(
      { id: r.id, status: next },
      {
        onSuccess: () =>
          notify({
            type: 'success',
            text:
              next === 'approved'
                ? `Added “${r.keyword}” to tracked keywords.`
                : `Rejected “${r.keyword}”.`,
          }),
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )

  return (
    <Card title="Keywords">
      <Stack spacing={4} sx={{ mt: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
        >
          <ViewToggle options={reviewOptions} value={review} onChange={setReview} ariaLabel="Keyword status" />
          {tracking ? (
            <Typography variant="body3" color="text.secondary">
              Tracked by rule
              <InfoTooltip term="seo-tracking-rule" />: {fmt(tracking.rule)} terms (+{fmt(tracking.ownIt)}{' '}
              own-it, +{fmt(tracking.manual)} set) · about {fmt(tracking.estimatedCredits)} credits a month
              here, {fmt(status.tracking.totalEstimatedCredits)} across markets (cap{' '}
              {fmt(status.tracking.creditCap)}, keyword metrics extra)
            </Typography>
          ) : null}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gap: '12px',
            // `minmax(0, 1fr)` on phones too: a bare `1fr` floors at the select's longest
            // option and pushed the column past the card at 390 wide.
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(6, minmax(0, 1fr))' },
          }}
        >
          {/* `minWidth: 0`: Search's own 300px floor is for a table toolbar; in this grid it
              ran past its cell (and the card, at a phone width). */}
          <Search value={query} onChange={setQuery} placeholder="Search keywords" sx={{ minWidth: 0 }} />
          <SelectField
            value={cluster}
            onChange={(v) => setCluster(v as string)}
            options={options(
              rows.map((r) => r.cluster),
              'All clusters',
            )}
          />
          <SelectField value={industry} onChange={(v) => setIndustry(v as string)} options={tagOptions('industry', 'All industries')} />
          <SelectField value={solution} onChange={(v) => setSolution(v as string)} options={tagOptions('solution', 'All solutions')} />
          <SelectField value={product} onChange={(v) => setProduct(v as string)} options={tagOptions('product', 'All products')} />
          <SelectField
            value={scope}
            onChange={(v) => setScope(v as 'all' | 'tracked' | 'own')}
            options={[
              { value: 'all', label: 'All keywords' },
              { value: 'tracked', label: 'Rank-tracked here' },
              { value: 'own', label: 'Marked own-it' },
            ]}
          />
        </Box>

        {isLoading ? (
          <Skeleton height={14} lines={10} />
        ) : error || !data ? (
          <SectionNote>Could not load keywords: {(error as Error | null)?.message}</SectionNote>
        ) : (
          <>
            <DemandPanel d={data.demand[review]} labels={labels} />
            <SectionNote>
              {fmt(filtered.length)} of {fmt(counts[review])} {REVIEW_LABEL[review].toLowerCase()} keywords
              {filtered.length > TOP ? `, showing the top ${TOP} by volume` : ''}. Trend is the last
              12 months of monthly volume; a dash means no data, not zero searches. Clicks and ad value
              are estimates on the CTR curve.
              {review === 'proposed'
                ? ' Proposed keywords spend nothing: approve one to add it to the vocabulary.'
                : ''}
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Keyword</HeadCell>
                  <HeadCell>Cluster</HeadCell>
                  <HeadCell>Priority</HeadCell>
                  <HeadCell align="right">Volume</HeadCell>
                  <HeadCell>Trend</HeadCell>
                  <HeadCell align="right">
                    KD
                    <InfoTooltip term="seo-keyword-difficulty" />
                  </HeadCell>
                  <HeadCell align="right">
                    CPC
                    <InfoTooltip term="seo-cpc" />
                  </HeadCell>
                  <HeadCell align="right">Vera</HeadCell>
                  <HeadCell align="right">
                    Clicks now / #1
                    <InfoTooltip term="seo-estimated-clicks" />
                  </HeadCell>
                  <HeadCell align="right">
                    Ad value #1
                    <InfoTooltip term="seo-equivalent-ad-value" />
                  </HeadCell>
                  {review === 'approved' ? (
                    <>
                      <HeadCell>Tracked</HeadCell>
                      <HeadCell>
                        Own it
                        <InfoTooltip term="seo-own-it" />
                      </HeadCell>
                    </>
                  ) : (
                    <HeadCell>Evidence</HeadCell>
                  )}
                  <HeadCell align="right"> </HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <EmptyRow colSpan={13} text="No keywords match these filters." />
                ) : (
                  filtered.slice(0, TOP).map((r) => (
                    <TableRow key={r.id}>
                      <Cell strong>
                        {r.keyword}
                        {r.language !== 'en' && r.translationOf ? (
                          <span style={{ fontWeight: 400, opacity: 0.6 }}> · {r.language}</span>
                        ) : null}
                        <ReviewChip status={r.status} />
                        <TagChips row={r} labels={labels} />
                      </Cell>
                      <Cell>{r.cluster ?? '—'}</Cell>
                      <Cell>{r.priority ?? '—'}</Cell>
                      <Cell align="right">{fmt(r.metrics?.volume)}</Cell>
                      <Cell>
                        <Stack direction="row" alignItems="center">
                          <Sparkline
                            values={(r.metrics?.trend ?? []).map((t) => t.volume)}
                            label={`${r.keyword} monthly volume`}
                          />
                          <TrendFlag flag={r.trendFlag} />
                        </Stack>
                      </Cell>
                      <Cell align="right">{fmt(r.metrics?.kd)}</Cell>
                      <Cell align="right">
                        {r.metrics?.cpc !== null && r.metrics?.cpc !== undefined
                          ? `$${r.metrics.cpc.toFixed(2)}`
                          : '—'}
                      </Cell>
                      <Cell align="right">
                        {r.veraPositionSource ? (
                          <InfoTooltip
                            term={r.veraPositionSource === 'gsc' ? 'gsc-average-position' : undefined}
                            text={
                              r.veraPositionSource === 'gsc'
                                ? 'Google Search Console 28-day average (no live check yet). Source: Google Search Console, synced nightly, 2–3 day lag.'
                                : 'Newest live rank check (SERP).'
                            }
                          >
                            <span style={{ cursor: 'help' }}>
                              {r.veraPosition === null ? '—' : r.veraPosition}
                            </span>
                          </InfoTooltip>
                        ) : (
                          <span>{r.veraPosition === null ? '—' : r.veraPosition}</span>
                        )}
                      </Cell>
                      <Cell align="right">
                        {fmt(r.estClicks.now)} / {fmt(r.estClicks.at1)}
                      </Cell>
                      <Cell align="right">{fmtUsd(r.adValue.at1)}</Cell>
                      {review === 'approved' ? (
                        <>
                          <Cell>
                            {r.trackReason ? (
                              <InfoTooltip
                                term={r.trackReason === 'own-it' ? 'seo-own-it' : 'seo-tracking-rule'}
                                text={
                                  r.trackReason === 'rule'
                                    ? 'Meets the tracking rule (buyer fit and searches).'
                                    : r.trackReason === 'own-it'
                                    ? 'Tracked because it is marked own-it.'
                                    : 'In this market’s explicit set (e.g. the FR/IT term sets).'
                                }
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={REASON_LABEL[r.trackReason] ?? r.trackReason}
                                  sx={{ height: 20, fontSize: 11, cursor: 'help' }}
                                />
                              </InfoTooltip>
                            ) : (
                              '—'
                            )}
                          </Cell>
                          <Cell>
                            <Switch
                              checked={r.ownIt}
                              disabled={!isAdmin || setOwnIt.isPending}
                              inputProps={{ 'aria-label': `Own ${r.keyword}` }}
                              onChange={(_: unknown, checked: boolean) =>
                                setOwnIt.mutate({ id: r.id, ownIt: checked })
                              }
                            />
                          </Cell>
                        </>
                      ) : (
                        <Cell>
                          <Box sx={{ whiteSpace: 'normal', minWidth: 200, maxWidth: 320, lineHeight: 1.4 }}>
                            {keywordEvidenceLine(r.evidence) ?? r.evidence?.note ?? r.notes ?? '—'}
                          </Box>
                        </Cell>
                      )}
                      <Cell align="right">
                        <KeywordMenu
                          keyword={r.keyword}
                          status={r.status}
                          pending={setStatus.isPending}
                          onSet={(next) => changeStatus(r, next)}
                          extraItems={[
                            ...marketItems(r),
                            {
                              label: 'Edit tags',
                              icon: <TagsIcon size={18} strokeWidth={1} />,
                              divider: true,
                              disabled: !isAdmin,
                              tooltip: !isAdmin ? ADMIN_ONLY : undefined,
                              onClick: () => setEditing(r),
                            },
                          ]}
                        />
                      </Cell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StatTable>
          </>
        )}
      </Stack>
      <EditTagsModal row={editing} taxonomy={status.taxonomy} onClose={() => setEditing(null)} />
    </Card>
  )
}
